<template>
  <nav
    :class="{
      'w-full fixed visible shrink-0 md:w-sidebar md:mr-2 whitespace-nowrap scrollbar-custom': ui.showStoreList,
      'invisible fixed w-0 mr-0 whitespace-nowrap': !ui.showStoreList
    }"
    class="fixed min-w-0 overflow-y-auto
          top-[291px] bottom-[32px]
          md:top-auto md:bottom-auto md:h-[78vh]
          [&>div]:last:[&>div]:border-b-0
          md:transform-gpu md:transition-all md:duration-500
          z-10 bg-white pr-0 pt-0 bp-1
          md:rounded-md md:m-2 opacity-100 md:opacity-95"
  >
    <div v-if="search.pending" class="overflow-x-hidden scrollbar-custom">
      <div v-for="n in 6" :key="n" class="px-8 pt-6 pb-4 space-y-2 border-b border-gray-700">
        <USkeleton class="h-4 w-[150px]" />
        <div class="flex justify-between">
          <USkeleton class="h-4 w-[120px]" />
          <USkeleton class="h-4 w-[60px]" />
        </div>
        <USkeleton class="h-5 w-[150px]" />
        <div class="flex justify-between gap-x-16">
          <USkeleton class="h-4 w-[150px]" />
          <USkeleton class="h-7 w-[75px] relative bottom-2" />
        </div>
      </div>
    </div>
    <VirtualScroller
      v-if="filteredStores.length > 0"
      :items="filteredStores"
      :itemSize="190"
      style="height: 100%"
      class="w-full overflow-x-hidden scrollbar-custom"
    >
      <template v-slot:item="{ item, options }">
        <StoreListCard
          :result="item"
          :showDistance="user.useMyLocation"
          :selected="item.id === map.selectedFeatureId"
          :key="item.id"
          @click="onStoreClicked(item.id)"
        />
      </template>
    </VirtualScroller>

    <div
      v-if="filteredStores.length === 0 && !search.pending"
      class="h-full p-8 pt-6 overflow-x-hidden bg-white text-darkPurple scrollbar-custom"
    >
      <h3 class="font-semibold">{{ t('noMatchingResults') }}</h3>
      <div class="mt-8 text-sm">{{ t('maybeYouCouldTry') }}</div>
      <ul class="mt-4 text-sm list-disc list-inside">
        <li>{{ t('doubleCheckingYourSpelling') }}</li>
        <li>{{ t('choosingADifferentLocation') }}</li>
        <li>{{ t('amendingYourChoiceOfStoreType') }}</li>
      </ul>
    </div>
  </nav>
</template>

<script setup lang="ts">
import VirtualScroller from 'primevue/virtualscroller'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import { computed } from 'vue'

const filteredStores = computed(() =>
  search.stores
    .filter(
      store =>
        store.isProfileNavigable === true &&
        store.truRating &&
        store.truRating.length > 0 &&
        store.ratingCount &&
        store.ratingCount > 0
    )
    .slice()
    .sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity))
)

const breakpoints = useBreakpoints(breakpointsTailwind)
const activeBreakpoints = breakpoints.active()
const smallViewport = computed(() => !activeBreakpoints.value || ['sm'].includes(activeBreakpoints.value))

const { t } = useI18n()
const map = useMapStore()
const search = useSearchStore()
const ui = useUiStore()
const user = useUserStore()
const gtm = useGtm()

function onStoreClicked(storeId: number) {
  if (!smallViewport.value) {
    const store = search.getStoreById(storeId)
    map.selectedFeatureId = store.id
    search.storeOrOutletSearchModel = undefined
    search.storeOrOutlet = undefined
    search.storeTypeIds = undefined
    ui.showMapView()
    gtm?.trackEvent({
      event: 'Store List Selected',
      category: 'discovery',
      action: 'User selected store from Store List',
      label: store.displayName
    })
  }
}
</script>
